// src/components/Chat/GuestInfoModal.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { User, Phone, X, ShoppingCart } from 'lucide-react';
import { signInAnonymously } from 'firebase/auth';
import { GuestAuthService } from '../../services/GuestAuthService';
import { auth } from '../../firebase/config';


const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: ${props => props.theme?.colors?.background || '#111'};
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 16px;
  padding: 2rem;
  max-width: 450px;
  width: 100%;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.text || '#FFF'};
  cursor: pointer;
  padding: 0.5rem;
  opacity: 0.7;
  transition: all 0.2s;
  
  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const Title = styled.h2`
  font-family: ${props => props.theme?.fonts?.heading || 'inherit'};
  color: ${props => props.theme?.colors?.accent || '#800000'};
  margin:  0 5rem ;
  font-size: 1.5rem;
  display: flex;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  color: ${props => props.theme?.colors?.text || '#FFF'};
  opacity: 0.8;
  margin: 0 0 1.5rem 0;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
  
  label {
    display: block;
    color: ${props => props.theme?.colors?.text || '#FFF'};
    font-weight: 600;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }
  
  .input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    
    svg {
      position: absolute;
      left: 1rem;
      color: ${props => props.theme?.colors?.accent || '#800000'};
      opacity: 0.6;
    }
    
    input {
      width: 100%;
      padding: 0.75rem 1rem 0.75rem 3rem;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(128, 0, 0, 0.3)'};
      border-radius: 8px;
      color: ${props => props.theme?.colors?.text || '#FFF'};
      font-size: 0.95rem;
      transition: all 0.3s;
      
      &:focus {
        outline: none;
        border-color: ${props => props.theme?.colors?.accent || '#800000'};
        background: rgba(255, 255, 255, 0.08);
      }
      
      &::placeholder {
        color: rgba(255, 255, 255, 0.4);
      }
    }
  }
  
  .hint {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 0.5rem;
    font-style: italic;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #F44336;
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${props => props.theme?.colors?.accent || '#800000'};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme?.colors?.primary || '#600000'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GuestInfoModal = ({ isOpen, onClose, onSubmit, theme, item }) => {
  const [firstName, setFirstName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const validateForm = () => {
    if (!firstName.trim()) {
      setError('Please enter your name');
      return false;
    }
    
    const cleanedPhone = phoneNumber.replace(/\D/g, '');
    if (cleanedPhone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
  setError('');
  
  if (!validateForm()) {
    return;
  }
  
  setLoading(true);
  
  try {
    const cleanedPhone = phoneNumber.replace(/\D/g, '');
    const displayName = firstName.trim();
    
    console.log('🔍 Checking for existing guest account with phone:', cleanedPhone);
    
    // ✅ STEP 1: Check if guest account already exists
    let guestAccount = await GuestAuthService.findGuestByPhone(cleanedPhone);
    
    if (guestAccount) {
      console.log('✅ Found existing guest account:', guestAccount.uid);
      
      // ✅ Sign in anonymously with Firebase (creates session)
      await signInAnonymously(auth);
      
      // ✅ Store the EXISTING guest session
      localStorage.setItem('guest_session', JSON.stringify({
        guestId: guestAccount.uid,
        phoneNumber: cleanedPhone,
        displayName: guestAccount.displayName || displayName,
        createdAt: guestAccount.createdAt || new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      }));
      
      console.log('✅ Existing guest session restored');
      
    } else {
      console.log('🆕 No existing account found, creating new guest account');
      
      // ✅ STEP 2: Create NEW guest account if none exists
      guestAccount = await GuestAuthService.createGuestAccount({
        firstName: displayName,
        phoneNumber: cleanedPhone,
        displayName: displayName
      });
      
      console.log('✅ New guest account created:', guestAccount.uid);
    }
    
    // Call original onSubmit with guest info
    await onSubmit({
      uid: guestAccount.uid,
      firstName: displayName,
      phoneNumber: cleanedPhone,
      displayName: guestAccount.displayName || displayName,
      isGuest: true
    });
    
    onClose();
  } catch (error) {
    console.error('Error with guest account:', error);
    setError(error.message || 'Failed to process account. Please try again.');
  } finally {
    setLoading(false);
  }
};

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()} theme={theme}>
        <CloseButton onClick={onClose} theme={theme}>
          <X size={20} />
        </CloseButton>
        
        <Title theme={theme}>
          <ShoppingCart size={24} />
          Guest Checkout
        </Title>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <FormGroup theme={theme}>
          <div className="input-wrapper">
            <User size={18} />
            <input
              type="text"
              placeholder="Full Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={50}
            />
          </div>
        </FormGroup>
        
        <FormGroup theme={theme}>
          <div className="input-wrapper">
            <Phone size={18} />
            <input
              type="tel"
              placeholder="Phone Number"
              value={phoneNumber}
              onChange={handlePhoneChange}
              maxLength={14}
            />
          </div>
          <div className="hint">
            *Used to access your order*
          </div>
        </FormGroup>
        
        <SubmitButton 
          onClick={handleSubmit} 
          disabled={loading}
          theme={theme}
        >
          {loading ? 'Creating Account...' : 'Place Order'}
        </SubmitButton>
      </ModalContent>
    </ModalOverlay>
  );
};

export default GuestInfoModal;